import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import AdminCompteRebours from "./AdminCompteRebours";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEvent() {
  const [form, setForm] = useState(null);
  const [programmeText, setProgrammeText] = useState("");
  const [galerieText, setGalerieText] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/event`).then((r) => {
      setForm(r.data);
      setProgrammeText(r.data.programme.map((p) => `${p.heure} | ${p.titre}`).join("\n"));
      setGalerieText((r.data.galerie || []).map((g) => `${g.type} | ${g.url} | ${g.legende || ""}`).join("\n"));
    }).catch(() => toast.error("Erreur de chargement de l'événement"));
  }, []);
  useEffect(load, [load]);

  if (!form) return <p className="text-[#71717A] text-sm">Chargement…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setGalerieText((t) => `${t}${t ? "\n" : ""}image | ${data.url} | `);
      toast.success("Image téléversée — complétez la légende puis enregistrez");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const programme = programmeText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [heure, ...rest] = l.split("|");
        return { heure: (heure || "").trim(), titre: rest.join("|").trim() };
      })
      .filter((p) => p.heure && p.titre);
    const galerie = galerieText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [type, url, legende] = l.split("|").map((s) => (s || "").trim());
        return { type: type || "image", url, legende: legende || null };
      })
      .filter((g) => g.url);
    try {
      await axios.put(`${API}/admin/event`, { ...form, programme, galerie }, { withCredentials: true });
      toast.success("Événement mis à jour");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-event-form" className="max-w-3xl">
      <p className="text-sm text-[#71717A] mb-8">
        Ces informations alimentent la page publique <a href="/evenement" className="text-[#C5A059] hover:underline">/evenement</a> et le compte à rebours.
      </p>
      <div className="grid sm:grid-cols-2 gap-8">
        <input data-testid="event-input-titre" required placeholder="Titre *" value={form.titre} onChange={set("titre")} className="input-editorial sm:col-span-2" />
        <input data-testid="event-input-sous-titre" required placeholder="Sous-titre (ex: U16 à U23 · Hommes & Femmes) *" value={form.sous_titre} onChange={set("sous_titre")} className="input-editorial sm:col-span-2" />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date et heure de l'événement (compte à rebours) *</label>
          <input data-testid="event-input-date" required type="datetime-local" value={form.date_event} onChange={set("date_event")} className="input-editorial [color-scheme:dark]" />
        </div>
        <input data-testid="event-input-lieu" required placeholder="Lieu (ex: Kinshasa, RDC) *" value={form.lieu} onChange={set("lieu")} className="input-editorial" />
        <input data-testid="event-input-adresse" required placeholder="Adresse / stade *" value={form.adresse} onChange={set("adresse")} className="input-editorial sm:col-span-2" />
        <textarea data-testid="event-input-description" required rows={4} placeholder="Description *" value={form.description} onChange={set("description")} className="input-editorial sm:col-span-2 resize-none" />
        <input data-testid="event-input-affiche" required placeholder="URL affiche (ex: /assets/affiche-event.png) *" value={form.affiche} onChange={set("affiche")} className="input-editorial sm:col-span-2" />
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Programme — une ligne par étape, format « heure | titre »</label>
          <textarea
            data-testid="event-input-programme"
            rows={6}
            value={programmeText}
            onChange={(e) => setProgrammeText(e.target.value)}
            placeholder={"08h00 | Accueil\n09h00 | Tests physiques"}
            className="input-editorial resize-none font-mono text-sm"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">
            Galerie — une ligne par média, format « type | url | légende » (type : image ou youtube)
          </label>
          <textarea
            data-testid="event-input-galerie"
            rows={5}
            value={galerieText}
            onChange={(e) => setGalerieText(e.target.value)}
            placeholder={"image | /api/media/... | Matchs du matin\nyoutube | https://youtube.com/watch?v=... | Résumé vidéo"}
            className="input-editorial mt-1 resize-none font-mono text-sm"
          />
          <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} data-testid="event-gallery-file" />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            data-testid="event-gallery-upload"
            className="mt-4 inline-flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Téléverser une image
          </button>
        </div>
      </div>
      <button type="submit" disabled={saving} data-testid="event-save-button" className="mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
        {saving && <Loader2 size={15} className="animate-spin" />}
        Enregistrer l'événement
      </button>
      <div className="mt-10">
        <AdminCompteRebours />
      </div>
    </form>
  );
}
